/**
 * Image optimization utilities for performance
 * Implements responsive sizing and format optimization
 */

export interface ImageOptimizationOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpg' | 'png';
}

/**
 * Optimize image URL with responsive sizing parameters
 * Works with Unsplash and other image CDNs that support query params
 */
export function optimizeImageUrl(
  url: string | null | undefined,
  options: ImageOptimizationOptions = {}
): string | null {
  if (!url) return null;
  
  const {
    width = 400,
    height = 300,
    quality = 80,
    format = 'webp'
  } = options;
  
  // Don't modify data URLs or relative paths
  if (url.startsWith('data:') || url.startsWith('/')) {
    return url;
  }
  
  // Add optimization params for Unsplash URLs
  if (url.includes('unsplash.com') || url.includes('images.unsplash.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${width}&h=${height}&q=${quality}&fm=${format}&fit=crop`;
  }
  
  // Add optimization params for Supabase Storage URLs
  if (url.includes('supabase.co/storage')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}&height=${height}&quality=${quality}&format=${format}`;
  }
  
  // Return as-is for other URLs
  return url;
}

/**
 * Get optimized sizes for different viewports
 */
export const imageSizes = {
  thumbnail: { width: 200, height: 150 },
  card: { width: 400, height: 300 },
  hero: { width: 1200, height: 800 },
  full: { width: 1920, height: 1080 },
};
